/**
 * AYARLAR › DEPOLAMA (kullanıcı kararı 19.10.2026;
 * `claude/notlar/2026-10-19-depolama-karari.md`).
 *
 * Tek soru: kasa **yalnızca bu cihazda** mı dursun, yoksa **bulut
 * depolamada** da şifreli bir kopyası mı olsun. Bulut seçilince sağlayıcı
 * listesi (Parafon sunucusu, Dropbox, Google Drive, OneDrive, iCloud,
 * eşitlenen klasör) açılır ve yalnızca SEÇİLENİN istediği alan görünür. Bir
 * kez bağlanılır; bulutta kopya varsa "getir ya da bu cihazdakiyle başla"
 * sorulur. Eski "kur" ve "geri yükle" bölümleri bu tek akışa indi (19.10
 * "Bulut yedeği tek ekran" kararı).
 *
 * Kasa bulut seçilse de cihazda da durur (internetsiz açılış); bulutta duran,
 * sağlayıcının açamadığı şifreli kopya. Ekranda bu tek satırla söylenir.
 *
 * AÇILIR PENCERE KURALI: tarayıcı açılır pencereyi ve klasör seçiciyi yalnızca
 * bir tıklamanın İÇİNDEN açar. Bu yüzden `baglan()` her işleyicide İLK
 * çağrılan şey; doğrulamalar ondan önce ve eşzamanlı yapılıyor. (Parafon
 * sunucusu açılır pencere açmadığı için onun doğrulaması beklenerek yapılır.)
 */

import { M, YEREL } from "../dil";
import { useEffect, useRef, useState } from "react";
import type { ImportResult } from "@parafon/contracts/local-api";
import { KASA_SAKLAMA_AY, KASA_SUNUCU_PAROLA_EN_AZ } from "@parafon/contracts/central-api";
import { downloadBlob } from "../lib/files";
import { isBaskaDefter } from "../lib/rpcError";
import { routeSorgusunuAl } from "../lib/router";
import { useRepositoryContext } from "../repo/RepositoryContext";
import { useMutation } from "../repo/useAsync";
import { kanonikKasaKodu, kasaKoduGoster } from "../yedek/hedefler/parafonSunucu";
import type { SunucuSecimi } from "../yedek/hedefler/parafonSunucu";
import { HEDEF_ADI } from "../yedek/tipler";
import type { BulutDosyasi, HedefTuru } from "../yedek/tipler";
import { useYedek } from "../yedek/YedekSaglayici";
import type { GeciciHedef, YedekBaglami } from "../yedek/YedekSaglayici";
import { Button, Field, InlineError, Notice, TextArea, TextInput, cx } from "../ui/primitives";
import { ImportReport } from "./ImportReport";
import { KasaKoduQr, QrOkut } from "./KasaQr";
import { useDepolamaSorusu } from "./useDepolamaSorusu";
import { useSinirlar } from "../kabiliyet/KabiliyetSaglayici";
import { hedefKilidi } from "../kabiliyet/kabiliyet";

const CAKISMA_TURU = M.yedek.depolama.cakismaTuru;

function zaman(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleString(YEREL, { dateStyle: "short", timeStyle: "short" });
}

function boyut(n: number | null): string {
  if (n === null) return "";
  return n < 1024 ? `${n} B` : `${(n / 1024).toFixed(1)} KB`;
}

/** Geri yüklemenin sonucu — kart düzeyinde tutulur (bkz. aşağı). */
interface YuklemeSonucu {
  readonly sonuc: ImportResult;
  /** D5: boş deftere v2 yüklendi, cihaz yedeğin anahtarını benimsedi. */
  readonly anahtarBenimsendi: boolean;
}

const BULUT_SATIRI = M.yedek.depolama.bulutSatiri;

export function DepolamaKarti({ yenidenYukle }: { readonly yenidenYukle?: () => void } = {}) {
  const y = useYedek();
  // Geri yüklemenin raporu KARTTA tutulur: geri yükleme otomatik kopyayı
  // başlatınca kart "kurulu" hâline geçer — rapor onunla kaybolmasın.
  const [geriYuklendi, setGeriYuklendi] = useState<YuklemeSonucu | null>(null);
  // İlk kurulumdaki soru açıksa burada da "Yalnızca bu cihazda kalsın" ile
  // cevaplanabilir — uyarı buraya getiriyor (19.10.2026).
  const soru = useDepolamaSorusu();
  const bolum = useRef<HTMLElement>(null);
  useEffect(() => {
    if (routeSorgusunuAl("bolum") === "depolama") bolum.current?.scrollIntoView?.({ block: "start" });
  }, []);
  if (y === null) return null;
  const { durum } = y;

  return (
    <section ref={bolum} id="depolama" data-kart-bolum="veri" className="pf-card scroll-mt-14 p-4" aria-labelledby="depolama-baslik">
      <h2 id="depolama-baslik" className="text-[14px] font-semibold text-ink">
        {M.yedek.depolama.baslik}
      </h2>
      {geriYuklendi !== null && <GeriYuklemeRaporu r={geriYuklendi} />}
      {/* Kasanın boş olup olmadığı bilinmeden çizilmez: yoksa ipucu bir an sonra belirirdi. */}
      {durum.kurulu === null || (durum.kurulu === false && y.defterBos === null) ? (
        <p className="mt-3 text-[12px] text-faint">{M.yedek.depolama.okunuyor}</p>
      ) : durum.kurulu ? (
        <>
          <KuruluDurum />
          <BuluttakiDosyalar onYuklendi={setGeriYuklendi} {...(yenidenYukle === undefined ? {} : { yenidenYukle })} />
        </>
      ) : (
        <DepolamaSecimi
          onYuklendi={setGeriYuklendi}
          {...(soru.bekliyor ? { onYalnizcaCihaz: soru.yalnizcaCihaz } : {})}
        />
      )}
    </section>
  );
}

function GeriYuklemeRaporu({ r }: { readonly r: YuklemeSonucu }) {
  return (
    <div className="mt-3">
      <ImportReport result={r.sonuc} title={M.yedek.depolama.geriYuklemeTamam} />
      {r.anahtarBenimsendi && (
        <div className="mt-2">
          <Notice tone="info" title={M.yedek.depolama.kilidiKullaniyor}>
            <p>{M.yedek.depolama.anahtarBenimsendi}</p>
          </Notice>
        </div>
      )}
    </div>
  );
}

/**
 * Kurulu değilken: "Yalnızca bu cihazda" / "Bulut depolama". Ana sayfadaki
 * ilk kurulum sorusu da (`DepolamaSorusu`) bunu kullanır; `onYalnizcaCihaz`
 * verilirse "Yalnızca bu cihazda" bir onay düğmesiyle kapanır.
 */
export function DepolamaSecimi({
  onYuklendi,
  onYalnizcaCihaz,
}: {
  readonly onYuklendi: (r: YuklemeSonucu) => void;
  readonly onYalnizcaCihaz?: () => void;
}) {
  const y = useYedek();
  const run = useMutation();
  const [yer, setYer] = useState<"cihaz" | "bulut">("cihaz");
  const [parola, setParola] = useState("");
  const [tekrar, setTekrar] = useState("");
  const [bulunan, setBulunan] = useState<{ h: GeciciHedef; dosyalar: readonly BulutDosyasi[] } | null>(null);
  if (y === null) return null;

  const onBaglanti = (baglanti: Promise<GeciciHedef>, parolaSinanmadi: boolean): void => {
    void run.run(async () => {
      const h = await baglanti;
      const dosyalar = await y.listele(h);
      if (parolaSinanmadi && dosyalar.length === 0) {
        throw new Error(M.yedek.depolama.koddaKasaYok);
      }
      if (y.defterBos === true && dosyalar.length > 0) {
        // Boş kasa + bulutta kopya: büyük ihtimalle kasayı GETİRMEK istiyor.
        setBulunan({ h, dosyalar });
        return;
      }
      if (!y.kilitli && parola.length === 0) throw new Error(M.yedek.depolama.yedekParolasiGerekli);
      await y.kur(h, y.kilitli ? undefined : parola);
      setParola("");
      setTekrar("");
    });
  };

  return (
    <div className="mt-3 space-y-3">
      <fieldset className="grid gap-2 text-[13px]">
        <legend className="sr-only">{M.yedek.depolama.kasaNeredeDursun}</legend>
        <label className={cx("flex gap-2 rounded-pf border border-line p-2", yer === "cihaz" && "border-accent bg-accent-soft")}>
          <input type="radio" name="depolama-yer" checked={yer === "cihaz"} onChange={() => setYer("cihaz")} />
          <span>
            <span className="font-semibold text-ink">{M.yedek.depolama.yalnizcaCihaz}</span>
            <span className="block text-[12px] text-muted">{M.yedek.depolama.yalnizcaCihazAciklama}</span>
          </span>
        </label>
        <label className={cx("flex gap-2 rounded-pf border border-line p-2", yer === "bulut" && "border-accent bg-accent-soft")}>
          <input type="radio" name="depolama-yer" checked={yer === "bulut"} onChange={() => setYer("bulut")} />
          <span>
            <span className="font-semibold text-ink">{M.yedek.depolama.bulutDepolama}</span>
            <span className="block text-[12px] text-muted">{BULUT_SATIRI}</span>
          </span>
        </label>
      </fieldset>

      {yer === "cihaz" && onYalnizcaCihaz !== undefined && (
        <Button variant="primary" onClick={onYalnizcaCihaz}>
          {M.yedek.depolama.yalnizcaCihazKalsin}
        </Button>
      )}

      {yer === "bulut" && (
        <>
          {y.defterBos === true && (
            <p className="text-[12px] text-muted">{M.yedek.depolama.baskaCihazdaKullandiysaniz}</p>
          )}
          {!y.kilitli && (
            <div className="grid gap-3 sm:grid-cols-2">
              <Field
                label={M.yedek.depolama.yedekParolasi}
                htmlFor="yedek-parola"
                hint={M.yedek.depolama.yedekParolasiIpucu}
              >
                <TextInput
                  id="yedek-parola"
                  type="password"
                  autoComplete="new-password"
                  value={parola}
                  onChange={(e) => setParola(e.currentTarget.value)}
                />
              </Field>
              <Field label={M.yedek.depolama.parolaTekrar} htmlFor="yedek-parola-tekrar">
                <TextInput
                  id="yedek-parola-tekrar"
                  type="password"
                  autoComplete="new-password"
                  value={tekrar}
                  onChange={(e) => setTekrar(e.currentTarget.value)}
                />
              </Field>
            </div>
          )}
          <SaglayiciSecici
            ad="depolama-saglayici"
            ucretliDenetim
            kodSecimi
            parolaSor={y.kilitli}
            {...(y.kilitli ? {} : { eskiParola: { parola, tekrar } })}
            mesgul={run.pending}
            bosKasa={y.defterBos === true}
            onBaglanti={onBaglanti}
            onHata={(e) => void run.run(() => Promise.reject(e))}
          />
          {run.error !== undefined && <InlineError error={run.error} />}
        </>
      )}

      {bulunan !== null && (
        <Notice tone="info" title={M.yedek.depolama.buluttaDosyaVar(bulunan.dosyalar.length)}>
          <p>{M.yedek.depolama.kasaBos}</p>
          <div className="mt-2">
            <DosyaSecVeYukle h={bulunan.h} dosyalar={bulunan.dosyalar} kurulu={false} onYuklendi={onYuklendi} />
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              onClick={() =>
                void run.run(async () => {
                  await y.kur(bulunan.h, y.kilitli ? undefined : parola);
                  setBulunan(null);
                })
              }
            >
              {M.yedek.depolama.buCihazdakiyleBasla}
            </Button>
          </div>
        </Notice>
      )}
    </div>
  );
}

/** Listede görünen seçenek: sağlayıcılar + iCloud (eşitlenen klasör yolundan). */
type SecenekKimligi = HedefTuru | "icloud";

const ICLOUD_NOTU = M.yedek.hedef.icloudNotu;

/** Sağlayıcı seçicinin dayandığı üç iş — yedek bağlamı ya da (kurulumda) bağlamsız kaynak. */
export interface BaglantiKaynagi {
  readonly secenekler: YedekBaglami["secenekler"];
  baglan(tur: HedefTuru, sunucu?: SunucuSecimi): Promise<GeciciHedef>;
  parolaDogrula(parola: string): Promise<boolean> | null;
}

/**
 * SAĞLAYICI SEÇİCİ — liste + seçilenin TEK alanı + tek "Bağlan" düğmesi.
 * Depolama seçimi, depolamayı değiştirme, "başka kasa aç" ve kurulumdaki
 * "Buluttan getir" bunu kullanır. `kaynak` verilmezse yedek bağlamı.
 */
export function SaglayiciSecici({
  kaynak,
  ad,
  kodSecimi,
  parolaSor,
  eskiParola,
  mesgul,
  dugme = M.yedek.depolama.baglan,
  baslik = M.yedek.depolama.bulutSaglayicisi,
  bosKasa = false,
  ucretliDenetim = false,
  onBaglanti,
  onHata,
}: {
  /** Gelir modeli: yeni hedef seçiminde ücretli hedefler kilitli (geri yüklemede değil). */
  readonly ucretliDenetim?: boolean;
  readonly kaynak?: BaglantiKaynagi;
  readonly ad: string;
  /** Parafon sunucusunda "yeni kod" seçeneği (getirmede yalnızca var olan kod). */
  readonly kodSecimi: boolean;
  /** Parafon sunucusunda kasa parolası sorulup 12 şartı doğrulansın mı. */
  readonly parolaSor: boolean;
  /** Eski kip (kilitsiz kasa): formdaki yedek parolası — sunucu şartı onunla sınanır. */
  readonly eskiParola?: { readonly parola: string; readonly tekrar: string };
  readonly mesgul: boolean;
  readonly dugme?: string;
  readonly baslik?: string;
  /**
   * Boş kasa: Parafon sunucusunda "Kodum var" seçilince parola SORULMAZ —
   * getirilen kasa kendi parolasıyla gelir. Klasör boş çıkarsa çağıran
   * kurmaz (`parolaSinanmadi`), şart böylece atlatılamaz.
   */
  readonly bosKasa?: boolean;
  /** Tıklamanın İÇİNDEN, ilk iş olarak başlatılmış bağlantı. */
  readonly onBaglanti: (b: Promise<GeciciHedef>, parolaSinanmadi: boolean) => void;
  readonly onHata: (e: unknown) => void;
}) {
  const yedek = useYedek();
  const y = kaynak ?? yedek;
  const { depolama } = useSinirlar();
  const [secili, setSecili] = useState<SecenekKimligi | null>(null);
  const [sunucu, setSunucu] = useState<SunucuDegeri>(kodSecimi ? BOS_SUNUCU : { ...BOS_SUNUCU, secim: "kod" });
  if (y === null) return null;

  const klasor = y.secenekler.find((s) => s.tur === "yerel-klasor");
  const secenekler: readonly { kimlik: SecenekKimligi; ad: string; engel: string | null; not: string }[] = [
    ...y.secenekler
      .filter((s) => s.tur !== "yerel-klasor")
      .map((s) => ({
        kimlik: s.tur as SecenekKimligi,
        ad: HEDEF_ADI[s.tur],
        engel: s.engel ?? (ucretliDenetim ? hedefKilidi(s.tur, depolama) : null),
        not: s.not,
      })),
    { kimlik: "icloud", ad: "iCloud", engel: klasor?.engel ?? null, not: ICLOUD_NOTU },
    ...(klasor === undefined
      ? []
      : [{ kimlik: "yerel-klasor" as const, ad: HEDEF_ADI["yerel-klasor"], engel: klasor.engel, not: klasor.not }]),
  ];
  const hicbiri = secenekler.every((s) => s.engel !== null);
  const parolaAtla = bosKasa && sunucu.secim === "kod";

  const onBaglan = (): void => {
    if (secili === null) {
      onHata(new Error(M.yedek.depolama.saglayiciSecin));
      return;
    }
    // Eski kip: boş parola geri yüklemeye izin verir (dosya kendi parolasıyla
    // açılır); yazılmışsa iki alan AÇILIR PENCEREDEN ÖNCE tutmalı.
    if (eskiParola !== undefined && eskiParola.parola.length > 0 && eskiParola.parola !== eskiParola.tekrar) {
      onHata(new Error(M.yedek.depolama.ikiParolaAyniDegil));
      return;
    }
    if (secili === "parafon-sunucu") {
      const sinanmaz = parolaAtla;
      // Açılır pencere yok: doğrulamalar sırayla, beklenerek yapılabilir.
      onBaglanti(
        (async () => {
          const secim = sunucuSecimi(sunucu);
          if (parolaSor && !sinanmaz) await sunucuParolasiniSina(y.parolaDogrula, true, sunucu.parola);
          else if (eskiParola !== undefined && eskiParola.parola.length > 0) {
            await sunucuParolasiniSina(y.parolaDogrula, false, eskiParola.parola);
          }
          return y.baglan("parafon-sunucu", secim);
        })(),
        sinanmaz && parolaSor,
      );
      return;
    }
    onBaglanti(y.baglan(secili === "icloud" ? "yerel-klasor" : secili), false); // ← tıklamanın içinde, ilk iş
  };

  return (
    <div className="space-y-3">
      <fieldset className="grid gap-2">
        <legend className="sr-only">{baslik}</legend>
        {secenekler.map((s) => (
          <label
            key={s.kimlik}
            className={cx(
              "flex gap-2 rounded-pf border border-line p-2 text-[13px]",
              s.engel === null ? "cursor-pointer" : "opacity-60",
              secili === s.kimlik && "border-accent bg-accent-soft",
            )}
          >
            <input
              type="radio"
              name={ad}
              value={s.kimlik}
              checked={secili === s.kimlik}
              disabled={s.engel !== null}
              onChange={() => setSecili(s.kimlik)}
            />
            <span>
              <span className="font-semibold text-ink">{s.ad}</span>
              <span className="block text-[12px] text-muted">{s.engel ?? s.not}</span>
            </span>
          </label>
        ))}
      </fieldset>
      {hicbiri ? (
        <Notice tone="warn" title={M.yedek.depolama.saglayiciYok}>
          <p>{M.yedek.depolama.saglayiciYokMetin}</p>
        </Notice>
      ) : (
        <>
          {secili === "parafon-sunucu" && (
            <SunucuAlanlari deger={sunucu} onDegis={setSunucu} kodSecimi={kodSecimi} parolaSor={parolaSor && !parolaAtla} />
          )}
          {secili !== null && (
            <Button variant="primary" disabled={mesgul} onClick={onBaglan}>
              {mesgul ? M.yedek.depolama.baglaniyor : dugme}
            </Button>
          )}
        </>
      )}
    </div>
  );
}

function KuruluDurum() {
  const y = useYedek();
  const simdiRun = useMutation();
  const baglanRun = useMutation();
  const degisRun = useMutation();
  const [islem, setIslem] = useState<"yok" | "degistir" | "cihaz">("yok");
  const [sunucuyuSil, setSunucuyuSil] = useState(true);
  if (y === null) return null;
  const { durum } = y;
  const sunucuda = durum.hedefTuru === "parafon-sunucu";

  const sunucuSorusu = sunucuda && (
    <label className="flex items-center gap-2 text-[12px] text-ink">
      <input type="checkbox" checked={sunucuyuSil} onChange={(e) => setSunucuyuSil(e.currentTarget.checked)} />
      {M.yedek.depolama.sunucuKopyasiSilinsin}
    </label>
  );

  return (
    <div className="mt-3 space-y-3">
      <p className="text-[12px] text-muted">{BULUT_SATIRI}</p>
      <dl className="grid gap-x-6 gap-y-1 text-[13px] sm:grid-cols-2">
        <dt className="text-muted">{M.yedek.depolama.nerede}</dt>
        <dd className="text-ink">
          {durum.hedefTuru === null ? "—" : HEDEF_ADI[durum.hedefTuru]}
          {durum.konum !== null && <span className="block text-[12px] text-faint">{durum.konum}</span>}
        </dd>
        <dt className="text-muted">{M.yedek.depolama.sonYedek}</dt>
        <dd className="pf-fig text-ink">
          {durum.son === null ? M.yedek.depolama.henuzYok : zaman(durum.son.zaman)}
          {durum.son !== null && <span className="block text-[12px] text-faint">{durum.son.dosya}</span>}
        </dd>
        {y.kilitli && (
          <>
            <dt className="text-muted">{M.yedek.depolama.oburCihazlardan}</dt>
            <dd className="pf-fig text-ink">
              {durum.esitleme === null
                ? M.yedek.depolama.henuzBakilmadi
                : durum.esitleme.dosya === 0
                  ? M.yedek.depolama.baskaCihazYok(zaman(durum.esitleme.zaman))
                  : M.yedek.depolama.degisiklikAlindi(durum.esitleme.alinan, zaman(durum.esitleme.zaman))}
            </dd>
          </>
        )}
      </dl>
      {durum.esitleme !== null && durum.esitleme.cakismalar.length > 0 && (
        <Notice tone="warn" title={M.yedek.depolama.cakismaBaslik(durum.esitleme.cakismalar.length)}>
          <p>{M.yedek.depolama.cakismaAciklama}</p>
          <ul className="mt-1 list-disc pl-5">
            {durum.esitleme.cakismalar.map((c) => (
              <li key={`${c.kind}:${c.id}`}>
                {M.yedek.depolama.cakismaSatiri(
                  CAKISMA_TURU[c.kind],
                  c.etiket,
                  c.kazanan === "bu-cihaz" ? M.yedek.depolama.buCihazdaki : M.yedek.depolama.oburCihazdaki,
                )}
              </li>
            ))}
          </ul>
          <p>{M.yedek.depolama.elleDuzeltin}</p>
        </Notice>
      )}

      {sunucuda && <KasaKoduBolumu />}
      {durum.baglantiGerekli && !sunucuda && (
        <Notice tone="warn" title={M.yedek.depolama.baglantiYenilenmeli}>
          <p>{M.yedek.depolama.baglantiYenilenmeliMetin}</p>
        </Notice>
      )}
      {durum.hata !== null && !durum.baglantiGerekli && (
        <Notice tone="danger" title={M.yedek.depolama.sonYedekYazilamadi}>
          <p>
            {zaman(durum.hata.zaman)}: {durum.hata.mesaj}
          </p>
          <p>{M.yedek.depolama.yenidenDenenir}</p>
        </Notice>
      )}
      {durum.bekliyor && !durum.baglantiGerekli && durum.hata === null && (
        <p className="text-[12px] text-muted">
          {durum.calisiyor ? M.yedek.depolama.yedekleniyor : M.yedek.depolama.degisiklikVar}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        {durum.baglantiGerekli && !sunucuda && (
          <Button
            variant="primary"
            disabled={baglanRun.pending}
            onClick={() => void baglanRun.run(() => y.yenidenBaglan())}
          >
            {durum.hedefTuru === null ? M.yedek.depolama.yenidenBaglan : M.yedek.depolama.hedefeYenidenBaglan(HEDEF_ADI[durum.hedefTuru])}
          </Button>
        )}
        <Button disabled={simdiRun.pending || durum.calisiyor} onClick={() => void simdiRun.run(() => y.simdi())}>
          {M.yedek.depolama.simdiYedekle}
        </Button>
        <Button onClick={() => setIslem(islem === "degistir" ? "yok" : "degistir")}>{M.yedek.depolama.depolamayiDegistir}</Button>
        <Button onClick={() => setIslem(islem === "cihaz" ? "yok" : "cihaz")}>{M.yedek.depolama.yalnizcaCihazaDon}</Button>
      </div>
      {simdiRun.error !== undefined && <InlineError error={simdiRun.error} />}
      {baglanRun.error !== undefined && <InlineError error={baglanRun.error} />}

      {islem === "degistir" && (
        <div className="space-y-2 rounded-pf border border-line p-3">
          <p className="text-[12px] text-muted">{M.yedek.depolama.yeniYerAciklama}</p>
          {sunucuSorusu}
          <SaglayiciSecici
            ad="depolama-degistir"
            ucretliDenetim
            kodSecimi
            parolaSor={y.kilitli}
            mesgul={degisRun.pending}
            dugme={M.yedek.depolama.baglanVeTasi}
            baslik={M.yedek.depolama.yeniBulutSaglayicisi}
            onBaglanti={(b) =>
              void degisRun
                .run(async () => {
                  await y.depolamayiDegistir(await b, sunucuyuSil);
                })
                .then((tamam) => {
                  if (tamam) setIslem("yok");
                })
            }
            onHata={(e) => void degisRun.run(() => Promise.reject(e))}
          />
        </div>
      )}
      {islem === "cihaz" && (
        <Notice tone="warn" title={M.yedek.depolama.yalnizcaCihazKalsinMi}>
          <p>{M.yedek.depolama.yalnizcaCihazKalsinMiMetin}</p>
          {sunucuSorusu}
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              variant="danger"
              disabled={degisRun.pending}
              onClick={() =>
                void degisRun.run(() => y.yalnizcaCihaza(sunucuyuSil)).then((tamam) => {
                  if (tamam) setIslem("yok");
                })
              }
            >
              {M.yedek.depolama.yalnizcaCihazKalsin}
            </Button>
            <Button onClick={() => setIslem("yok")}>{M.ortak.vazgec}</Button>
          </div>
        </Notice>
      )}
      {degisRun.error !== undefined && <InlineError error={degisRun.error} />}
    </div>
  );
}

/** Kurulu depolamadaki dosyalardan geri yükleme ya da "yerine koy" — kapalı bölüm. */
function BuluttakiDosyalar({
  onYuklendi,
  yenidenYukle,
}: {
  readonly onYuklendi: (r: YuklemeSonucu) => void;
  readonly yenidenYukle?: () => void;
}) {
  const y = useYedek();
  const listeRun = useMutation();
  const [baglanti, setBaglanti] = useState<{ h: GeciciHedef; dosyalar: readonly BulutDosyasi[] } | null>(null);
  const [degistir, setDegistir] = useState(false);
  if (y === null) return null;

  const onListele = (): void => {
    void listeRun.run(async () => {
      const g = await y.kuruluHedef();
      if (g === null) throw new Error(M.yedek.depolama.kuruluBulutYok);
      setBaglanti({ h: g, dosyalar: await y.listele(g) });
    });
  };

  return (
    <details className="mt-4 border-t border-line pt-3">
      <summary className="cursor-pointer text-[13px] font-semibold text-ink">{M.yedek.depolama.buluttakiDosyalar}</summary>
      <div className="mt-3 space-y-3">
        <Button variant="ghost" disabled={listeRun.pending} onClick={onListele}>
          {M.yedek.depolama.dosyalariListele}
        </Button>
        {listeRun.error !== undefined && <InlineError error={listeRun.error} />}
        {baglanti !== null &&
          (degistir ? (
            <DefterDegistir
              baglanti={baglanti}
              onVazgec={() => setDegistir(false)}
              {...(yenidenYukle === undefined ? {} : { yenidenYukle })}
            />
          ) : (
            <DosyaSecVeYukle
              h={baglanti.h}
              dosyalar={baglanti.dosyalar}
              kurulu
              onYuklendi={onYuklendi}
              onBaskaDefter={() => setDegistir(true)}
            />
          ))}
      </div>
    </details>
  );
}

// ---------------------------------------------------------------------------
// PARAFON SUNUCUSU — kasa kodu (kullanıcı kararları 16–17.10.2026)
// ---------------------------------------------------------------------------

interface SunucuDegeri {
  readonly secim: "yeni" | "kod";
  readonly kod: string;
  /** Kilitli kasada: o anki kasa parolası (12 karakter şartı doğrulanarak sınanır). */
  readonly parola: string;
}

const BOS_SUNUCU: SunucuDegeri = { secim: "yeni", kod: "", parola: "" };

function sunucuSecimi(d: SunucuDegeri): SunucuSecimi {
  if (d.secim === "yeni") return { yeni: true };
  if (d.kod.trim().length === 0) throw new Error(M.yedek.depolama.kasaKodunuGirin);
  return { kod: d.kod };
}

const KISA_PAROLA = M.yedek.depolama.kisaParola(KASA_SUNUCU_PAROLA_EN_AZ);

/**
 * 12 karakter ŞARTI (yalnızca bu hedefte). Parola hiçbir yerde saklanmadığı
 * için o anki parola girilir ve kasanın parolası olduğu DOĞRULANIR — yoksa
 * "uzun bir şey yaz, geç" şartı boşa çıkarırdı.
 */
async function sunucuParolasiniSina(
  dogrula: (p: string) => Promise<boolean> | null,
  kilitli: boolean,
  parola: string,
): Promise<void> {
  if (parola.length === 0) throw new Error(kilitli ? M.yedek.depolama.kasaParolanizGirin : M.yedek.saglayici.parolaGerekli);
  if ([...parola].length < KASA_SUNUCU_PAROLA_EN_AZ) throw new Error(KISA_PAROLA);
  if (!kilitli) return;
  const sonuc = dogrula(parola);
  if (sonuc === null) throw new Error(M.yedek.depolama.dogrulanamiyor);
  if (!(await sonuc)) throw new Error(M.yedek.depolama.parolaYanlis);
}

function SunucuAlanlari({
  deger,
  onDegis,
  kodSecimi,
  parolaSor,
}: {
  readonly deger: SunucuDegeri;
  readonly onDegis: (d: SunucuDegeri) => void;
  /** Kurulumda: yeni kod mu, var olan kod mu. Geri yüklemede yalnızca kod. */
  readonly kodSecimi: boolean;
  readonly parolaSor: boolean;
}) {
  return (
    <div className="space-y-3 rounded-pf border border-line p-3">
      {kodSecimi && (
        <fieldset className="grid gap-1 text-[13px]">
          <legend className="sr-only">{M.yedek.depolama.kasaKodu}</legend>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="sunucu-secim"
              checked={deger.secim === "yeni"}
              onChange={() => onDegis({ ...deger, secim: "yeni" })}
            />
            {M.yedek.depolama.yeniKodOlustur}
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="sunucu-secim"
              checked={deger.secim === "kod"}
              onChange={() => onDegis({ ...deger, secim: "kod" })}
            />
            {M.yedek.depolama.baskaCihazinKodu}
          </label>
        </fieldset>
      )}
      {(deger.secim === "kod" || !kodSecimi) && (
        <Field
          label={M.yedek.depolama.kasaKodu}
          htmlFor={kodSecimi ? "sunucu-kod" : "geri-sunucu-kod"}
          hint={M.yedek.depolama.kasaKoduIpucu}
        >
          <TextInput
            id={kodSecimi ? "sunucu-kod" : "geri-sunucu-kod"}
            autoComplete="off"
            spellCheck={false}
            value={deger.kod}
            onChange={(e) => onDegis({ ...deger, kod: e.currentTarget.value })}
          />
        </Field>
      )}
      {(deger.secim === "kod" || !kodSecimi) && <QrOkut onKod={(kod) => onDegis({ ...deger, kod })} />}
      {parolaSor && (
        <Field
          label={M.yedek.depolama.kasaParolaniz}
          htmlFor="sunucu-parola"
          hint={M.yedek.depolama.kasaParolanizIpucu(KASA_SUNUCU_PAROLA_EN_AZ)}
        >
          <TextInput
            id="sunucu-parola"
            type="password"
            autoComplete="current-password"
            value={deger.parola}
            onChange={(e) => onDegis({ ...deger, parola: e.currentTarget.value })}
          />
        </Field>
      )}
      <p className="text-[12px] text-muted">{M.yedek.depolama.kodAdres(KASA_SAKLAMA_AY)}</p>
    </div>
  );
}

/** Kurulu sunucu hedefi: kodu göster/kopyala, yenile, öbür cihazın yeni kodunu gir. */
function KasaKoduBolumu() {
  const y = useYedek();
  const [kod, setKod] = useState<string | null>(null);
  const [goster, setGoster] = useState(false);
  const [kopyalandi, setKopyalandi] = useState(false);
  const [qr, setQr] = useState(false);
  const [yenileSor, setYenileSor] = useState(false);
  const [yeniKod, setYeniKod] = useState("");
  const yenileRun = useMutation();
  const degistirRun = useMutation();
  const kopyaRun = useMutation();
  useEffect(() => {
    let iptal = false;
    void y
      ?.sunucuKodu()
      .then((k) => {
        if (!iptal) setKod(k);
      })
      .catch(() => undefined);
    return () => {
      iptal = true;
    };
  }, [y]);
  if (y === null) return null;
  const gosterim = kod === null ? "—" : kasaKoduGoster(kod);

  return (
    <div className="space-y-2 rounded-pf border border-line p-3 text-[13px]">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-muted">{M.yedek.depolama.kasaKodu}</span>
        <code className="pf-fig text-ink" data-testid="kasa-kodu">
          {goster ? gosterim : "•••••-•••••-•••••-•••••-••••••"}
        </code>
        <Button onClick={() => setGoster((g) => !g)}>{goster ? M.yedek.depolama.gizle : M.yedek.depolama.goster}</Button>
        <Button disabled={kod === null} onClick={() => setQr((q) => !q)}>
          {qr ? M.yedek.depolama.qrGizle : M.yedek.depolama.qrGoster}
        </Button>
        <Button
          disabled={kod === null || kopyaRun.pending}
          onClick={() =>
            void kopyaRun.run(async () => {
              await globalThis.navigator.clipboard.writeText(gosterim);
              setKopyalandi(true);
            })
          }
        >
          {kopyalandi ? M.yedek.depolama.kopyalandi : M.yedek.depolama.kopyala}
        </Button>
      </div>
      <p className="text-[12px] text-muted">{M.yedek.depolama.baskaCihazdaAc(KASA_SAKLAMA_AY)}</p>
      {qr && kod !== null && (
        <div className="space-y-1">
          <KasaKoduQr kod={kod} />
          <p className="text-[12px] text-muted">{M.yedek.depolama.qrOkutun}</p>
        </div>
      )}
      {kopyaRun.error !== undefined && <InlineError error={kopyaRun.error} />}

      {y.durum.baglantiGerekli && (
        <Notice tone="warn" title={M.yedek.depolama.kodBulunamadi}>
          <p>{M.yedek.depolama.kodBulunamadiMetin(KASA_SAKLAMA_AY)}</p>
          <div className="mt-2 flex flex-wrap items-end gap-2">
            <Field label={M.yedek.depolama.yeniKasaKodu} htmlFor="yeni-kasa-kodu">
              <TextInput
                id="yeni-kasa-kodu"
                autoComplete="off"
                spellCheck={false}
                value={yeniKod}
                onChange={(e) => setYeniKod(e.currentTarget.value)}
              />
            </Field>
            <QrOkut onKod={setYeniKod} />
            <Button
              variant="primary"
              disabled={degistirRun.pending}
              onClick={() =>
                void degistirRun.run(async () => {
                  if (kanonikKasaKodu(yeniKod) === null) throw new Error(M.yedek.depolama.kodHatali);
                  await y.koduDegistir(yeniKod);
                  setKod(await y.sunucuKodu());
                  setYeniKod("");
                })
              }
            >
              {M.yedek.depolama.koduKaydet}
            </Button>
          </div>
          {degistirRun.error !== undefined && <InlineError error={degistirRun.error} />}
        </Notice>
      )}

      {!yenileSor ? (
        <Button onClick={() => setYenileSor(true)}>{M.yedek.depolama.koduYenile}</Button>
      ) : (
        <Notice tone="warn" title={M.yedek.depolama.yenilensinMi}>
          <p>{M.yedek.depolama.yenilensinMiMetin}</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button
              variant="danger"
              disabled={yenileRun.pending}
              onClick={() =>
                void yenileRun
                  .run(async () => {
                    setKod(await y.koduYenile());
                    setGoster(true);
                    setKopyalandi(false);
                  })
                  .then((tamam) => {
                    if (tamam) setYenileSor(false);
                  })
              }
            >
              {yenileRun.pending ? M.yedek.depolama.yenileniyor : M.yedek.depolama.yenile}
            </Button>
            <Button onClick={() => setYenileSor(false)}>{M.ortak.vazgec}</Button>
          </div>
        </Notice>
      )}
      {yenileRun.error !== undefined && <InlineError error={yenileRun.error} />}
    </div>
  );
}

/**
 * Bağlantı kurulmuş, dosyalar listelenmiş: seç, parolayı gir, geri yükle.
 * Hem geri yükleme bölümü hem kurulumdaki "bulutta yedek var" kutusu bunu
 * kullanıyor — ikinci kez bağlanmak gerekmesin diye bağlantı dışarıdan gelir.
 */
function DosyaSecVeYukle({
  h,
  dosyalar,
  kurulu,
  onYuklendi,
  onBaskaDefter,
  degistir,
}: {
  readonly h: GeciciHedef;
  readonly dosyalar: readonly BulutDosyasi[];
  readonly kurulu: boolean;
  readonly onYuklendi?: (r: YuklemeSonucu) => void;
  /** Yedek başka bir deftere ait (BASKA_DEFTER): "yerine koy" yoluna geçiş. */
  readonly onBaskaDefter?: () => void;
  /** D6 kipi: geri yükleme değil, defteri DEĞİŞTİRME. */
  readonly degistir?: (r: { sonuc: ImportResult }) => void;
}) {
  const y = useYedek();
  const yukleRun = useMutation();
  const [secili, setSecili] = useState<string | null>(dosyalar[0]?.ad ?? null);
  const [parola, setParola] = useState("");
  const [kip, setKip] = useState<"parola" | "ifade">("parola");
  const [ifade, setIfade] = useState("");
  if (y === null) return null;
  const ifadeyle = y.kilitli && kip === "ifade";

  const onYukle = (): void => {
    if (secili === null) return;
    void yukleRun.run(async () => {
      const kelimeler = ifade.split(/\s+/).filter((w) => w.length > 0);
      if (degistir !== undefined) {
        const r = await y.defteriDegistir(h, secili, ifadeyle ? { kelimeler } : { parola });
        setParola("");
        setIfade("");
        degistir(r);
        return;
      }
      const sir = ifadeyle ? { kelimeler } : y.kilitli ? { parola } : parola;
      const r = await y.geriYukle(h, secili, sir);
      onYuklendi?.({ sonuc: r.sonuc, anahtarBenimsendi: r.anahtarBenimsendi });
      // Kurulu değilse otomatik yedek KENDİLİĞİNDEN başlar (kullanıcı kararı
      // 06.10). Eski kipte aynı parola + dosyanın tuzu = aynı anahtar;
      // kilitli defterde yedek ana anahtarla, parola gerekmez.
      if (!kurulu) await y.kur(h, y.kilitli ? undefined : parola, r.tuz ?? undefined);
      setParola("");
      setIfade("");
    });
  };

  if (dosyalar.length === 0) {
    return <p className="text-[12px] text-muted">{M.yedek.depolama.pfaiYok}</p>;
  }
  return (
    <div className="space-y-3">
      <fieldset className="grid max-h-56 gap-1 overflow-auto">
        <legend className="sr-only">{M.yedek.depolama.yedekDosyasi}</legend>
        {dosyalar.map((d) => (
          <label key={d.ad} className="flex items-baseline gap-2 text-[12px]">
            <input
              type="radio"
              name="geri-dosya"
              value={d.ad}
              checked={secili === d.ad}
              onChange={() => setSecili(d.ad)}
            />
            <span className="pf-fig text-ink">{d.ad}</span>
            <span className="text-faint">
              {zaman(d.degisti)} {boyut(d.boyut)}
            </span>
          </label>
        ))}
      </fieldset>
      {y.kilitli && (
        <div className="flex gap-3 text-[12px]" role="radiogroup" aria-label={M.yedek.depolama.yedegiNeyleAcacaksiniz}>
          <label className="flex items-center gap-1">
            <input type="radio" name="geri-kip" checked={kip === "parola"} onChange={() => setKip("parola")} />
            {M.kilit.getir.parolayla}
          </label>
          <label className="flex items-center gap-1">
            <input type="radio" name="geri-kip" checked={kip === "ifade"} onChange={() => setKip("ifade")} />
            {M.kilit.getir.ifadeyle}
          </label>
        </div>
      )}
      {ifadeyle ? (
        <Field label={M.kilit.kurtarmaIfadesi} htmlFor="geri-ifade" hint={M.kilit.getir.ifadeIpucu}>
          <TextArea
            id="geri-ifade"
            rows={3}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            value={ifade}
            onChange={(e) => setIfade(e.currentTarget.value)}
          />
        </Field>
      ) : (
        <Field
          label={y.kilitli ? M.yedek.depolama.kasaninParolasi : M.yedek.depolama.yedeginParolasi}
          htmlFor="geri-parola"
          {...(y.kilitli ? { hint: M.yedek.depolama.eskiBicimIpucu } : {})}
        >
          <TextInput
            id="geri-parola"
            type="password"
            autoComplete="current-password"
            value={parola}
            onChange={(e) => setParola(e.currentTarget.value)}
          />
        </Field>
      )}
      <Button
        variant="primary"
        disabled={yukleRun.pending || secili === null || (ifadeyle ? ifade.trim().length === 0 : parola.length === 0)}
        onClick={onYukle}
      >
        {yukleRun.pending ? M.yedek.depolama.cozuluyor : degistir !== undefined ? M.yedek.depolama.bununlaDegistir : M.yedek.depolama.geriYukle}
      </Button>
      {yukleRun.error !== undefined &&
        (isBaskaDefter(yukleRun.error) && degistir === undefined ? (
          <Notice tone="warn" title={M.yedek.depolama.baskaKasaya}>
            <p>{M.yedek.depolama.baskaKasayaMetin}</p>
            <p>{M.yedek.depolama.yerineKoyabilirsiniz}</p>
            {onBaskaDefter !== undefined && (
              <div className="mt-2">
                <Button onClick={onBaskaDefter}>{M.yedek.depolama.yerineKoy}</Button>
              </div>
            )}
          </Notice>
        ) : (
          <InlineError error={yukleRun.error} />
        ))}
    </div>
  );
}

/**
 * D6 · BU CİHAZDA BAŞKA BİR DEFTER AÇ (kullanıcı kararı 08.10.2026: "yerine
 * koy"). Sıra: ne silineceği söylenir → eski defter dosyaya indirilir ya da
 * bilerek atlanır → buluttaki dosya ve o defterin sırrı → değişim → sayfa
 * yeniden yüklenir (kilit/profil değişimi kuralı). Eski defteri saklamanın
 * yolu DOSYA ya da buluttaki eski yedek: bulut adları deftere bağlı olduğu
 * için yeni defter eski defterin dosyalarının üstüne yazılmaz.
 */
export function DefterDegistir({
  baglanti,
  onVazgec,
  yenidenYukle = () => globalThis.location?.reload(),
}: {
  readonly baglanti?: { readonly h: GeciciHedef; readonly dosyalar: readonly BulutDosyasi[] };
  readonly onVazgec: () => void;
  readonly yenidenYukle?: () => void;
}) {
  const y = useYedek();
  const { repo } = useRepositoryContext();
  const [sayilar, setSayilar] = useState<{ hesap: number; hareket: number } | null>(null);
  const [indirildi, setIndirildi] = useState(false);
  const [kaydetmeden, setKaydetmeden] = useState(false);
  const [kaynak, setKaynak] = useState(baglanti ?? null);
  const [bitti, setBitti] = useState<{ sonuc: ImportResult } | null>(null);
  const indirRun = useMutation();
  const listeRun = useMutation();

  useEffect(() => {
    let iptal = false;
    // Kasanın gerçek doluluğu: devre dışı hesaplar da sayılır (yalnızca görünüm, 25.10.2026).
    void Promise.all([repo.accounts.list(false, true), repo.transactions.query({ limit: 1, devreDisiDahil: true })]).then(
      ([h, t]) => {
        if (!iptal) setSayilar({ hesap: h.length, hareket: t.total });
      },
      () => undefined,
    );
    return () => {
      iptal = true;
    };
  }, [repo]);

  if (y === null || !y.kilitli || repo.sifreliYedek === undefined) return null;
  const sifreli = repo.sifreliYedek;
  const devam = indirildi || kaydetmeden;

  if (bitti !== null) {
    return (
      <section aria-label={M.yedek.depolama.kasaDegisti} className="mt-3 space-y-3">
        <ImportReport result={bitti.sonuc} title={M.yedek.depolama.kasaDegisti} />
        <Notice tone="info" title={M.yedek.depolama.kilidiKullaniyor}>
          <p>{M.yedek.depolama.degistiKilit}</p>
          <p>{M.yedek.depolama.degistiYedek}</p>
        </Notice>
        <Button variant="primary" onClick={yenidenYukle}>
          {M.yedek.depolama.yeniKasayiAc}
        </Button>
      </section>
    );
  }

  const onIndir = (): void => {
    void indirRun.run(async () => {
      const metin = await sifreli.olustur();
      downloadBlob(
        new Blob([metin], { type: "application/octet-stream" }),
        `parafon-eski-kasa-${new Date().toISOString().slice(0, 10)}.pfai`,
      );
      setIndirildi(true);
    });
  };

  const onBaglanti = (b: Promise<GeciciHedef>): void => {
    void listeRun.run(async () => {
      const h = await b;
      setKaynak({ h, dosyalar: await y.listele(h) });
    });
  };


  return (
    <section aria-labelledby="kasa-degistir-baslik" className="mt-3 space-y-3">
      <h3 id="kasa-degistir-baslik" className="text-[13px] font-semibold text-ink">
        {M.kilit.kart.baskaKasaAc}
      </h3>
      <Notice tone="warn" title={M.yedek.depolama.silinecek}>
        <p>
          {M.yedek.depolama.silinecekOn}
          {sayilar !== null && (
            <>
              {" "}
              (<strong>{M.yedek.depolama.silinecekSayi(sayilar.hesap, sayilar.hareket)}</strong>)
            </>
          )}{" "}
          {M.yedek.depolama.silinecekSon}
        </p>
        <p>{M.yedek.depolama.kilitDegisir}</p>
      </Notice>

      <div className="space-y-2">
        <p className="text-[12px] text-muted">{M.yedek.depolama.onceSaklayin}</p>
        <div className="flex flex-wrap items-center gap-2">
          <Button disabled={indirRun.pending} onClick={onIndir}>
            {indirildi ? M.yedek.depolama.eskiIndirildi : M.yedek.depolama.eskiIndir}
          </Button>
        </div>
        {indirRun.error !== undefined && <InlineError error={indirRun.error} />}
        {!indirildi && (
          <label className="flex items-center gap-2 text-[12px]">
            <input type="checkbox" checked={kaydetmeden} onChange={(e) => setKaydetmeden(e.currentTarget.checked)} />
            {M.yedek.depolama.kaydetmedenDevam}
          </label>
        )}
      </div>

      {devam &&
        (kaynak === null ? (
          <div className="space-y-2">
            <SaglayiciSecici
              ad="degistir-hedef"
              kodSecimi={false}
              parolaSor={false}
              mesgul={listeRun.pending}
              dugme={M.kilit.getir.baglanVeListele}
              baslik={M.yedek.depolama.getirilecekNerede}
              onBaglanti={onBaglanti}
              onHata={(e) => void listeRun.run(() => Promise.reject(e))}
            />
            {listeRun.error !== undefined && <InlineError error={listeRun.error} />}
          </div>
        ) : (
          <DosyaSecVeYukle h={kaynak.h} dosyalar={kaynak.dosyalar} kurulu degistir={setBitti} />
        ))}

      <Button variant="ghost" onClick={onVazgec}>
        {M.ortak.vazgec}
      </Button>
    </section>
  );
}
